package com.adoptu.frontend

import kotlinx.browser.window
import kotlin.js.Promise
import kotlin.js.json

private const val REFRESH_PATH = "/api/auth/refresh"

private fun rejectWithApiError(r: dynamic): Promise<dynamic> =
    r.text().then { text ->
        val message = try {
            JSON.parse<dynamic>(text.unsafeCast<String>()).error?.unsafeCast<String>()
                ?: "Request failed: $text"
        } catch (e: dynamic) {
            "Request failed: $text"
        }
        throw js("new Error(message)")
    }.unsafeCast<Promise<dynamic>>()

// Single-flight refresh: /api/auth/refresh rotates the refresh-token cookie and revokes the old
// one, so concurrent 401s (a page firing /api/auth/me plus its own data calls right after the
// 15-minute access token expires) each POSTing their own refresh would race - the first rotates,
// the rest present an already-revoked token and fail (and can trip reuse detection, revoking the
// whole session). Every caller shares the one in-flight request instead; resolves to whether the
// refresh succeeded.
private var inFlightRefresh: Promise<Boolean>? = null

internal fun refreshSession(): Promise<Boolean> {
    inFlightRefresh?.let { return it }
    val p = window.asDynamic().fetch(REFRESH_PATH, js("({method: 'POST', credentials: 'include'})"))
        .then { res: dynamic -> res.ok == true }
        .catch { false }
        .unsafeCast<Promise<Boolean>>()
    inFlightRefresh = p
    p.then { inFlightRefresh = null }
    return p
}

fun apiFetch(path: String, init: dynamic = null): Promise<dynamic> = apiFetchInternal(path, init, alreadyRetried = false)

/**
 * Silent-refresh-on-401: the AuthKit access-token cookie expires after 15 minutes (see
 * accessTokenExpiryMs in AuthRoutes.kt) - without this, a still-active user gets logged out of
 * whatever they're doing mid-session, well before the 7-day session they think they have. On a
 * 401, POSTs /api/auth/refresh (which rotates the refresh-token cookie into a fresh access-token
 * cookie) and retries the original request exactly once (`alreadyRetried` guards against looping
 * if the retried call somehow 401s again). Never retries the refresh call itself, or a 401 there
 * would recurse forever - that 401 is the real "you're logged out" signal, left alone.
 */
private fun apiFetchInternal(path: String, init: dynamic, alreadyRetried: Boolean): Promise<dynamic> {
    val opts = init ?: js("({})")
    val method = opts.method?.unsafeCast<String>()
    if (method == null) {
    } else if (method != "GET" && method != "HEAD") {
        if (opts.headers == null) {
            opts.headers = js("({})")
        }
        if (opts.headers["Content-Type"] == null && opts.headers["content-type"] == null) {
            opts.headers["Content-Type"] = "application/json"
        }
    }
    return window.asDynamic().fetch(path, opts).then { res ->
        try {
            val r = res.unsafeCast<dynamic>()
            if (r.status == 401 && path != REFRESH_PATH && !alreadyRetried) {
                refreshSession().then { ok ->
                    if (ok) {
                        apiFetchInternal(path, init, alreadyRetried = true)
                    } else {
                        rejectWithApiError(r)
                    }
                }
            } else if (!r.ok) {
                rejectWithApiError(r)
            } else {
                r.json().then<dynamic> { json -> json }
            }
        } catch (e: dynamic) {
            throw js("new Error('Request failed: ' + e)")
        }
    }
}

@JsExport
@JsName("ApiClient")
object ApiClientModule {
    // /api/auth/me is a soft-fail check (200 + {authenticated: false}), never a 401 - the retry
    // in apiFetch/apiFetchInternal only ever triggers on a real 401, so it does nothing here. Every
    // page's own auth guard reads this response directly (see e.g. ProfilePage.kt's
    // `if (user.authenticated == false) window.location.href = "/login"`), so without this, a page
    // load/reload after the 15-minute access token expires bounces straight to /login even though
    // the refresh token is still good. One refresh-and-recheck, scoped to just this accessor -
    // deliberately not changing /api/auth/me's own status-code contract, which every other page
    // already assumes is always 200.
    private var sessionCheck: Promise<dynamic>? = null

    fun me(): Promise<dynamic> = sessionCheck ?: fetchMe().also { sessionCheck = it }

    private fun fetchMe(): Promise<dynamic> = apiFetch("/api/auth/me").then<dynamic> { user ->
        if (user?.authenticated == false) {
            refreshSession().then { ok ->
                if (ok) apiFetch("/api/auth/me") else user
            }
        } else {
            user
        }
    }

    fun logout(): Promise<dynamic> {
        sessionCheck = null
        return apiFetch("/api/auth/logout", js("({method: 'POST'})"))
    }

    fun detectCountry(locale: String? = null): Promise<dynamic> {
        val query = if (!locale.isNullOrEmpty()) "?locale=" + window.asDynamic().encodeURIComponent(locale) else ""
        return apiFetch("/api/detect-country$query")
    }

    fun getPets(type: String? = null, country: String? = null): Promise<dynamic> {
        val params = mutableListOf<String>()
        if (!type.isNullOrEmpty()) params.add("type=" + window.asDynamic().encodeURIComponent(type))
        if (!country.isNullOrEmpty()) params.add("country=" + window.asDynamic().encodeURIComponent(country))
        val query = if (params.isNotEmpty()) "?" + params.joinToString("&") else ""
        return apiFetch("/api/pets$query")
    }

    fun getPet(id: String): Promise<dynamic> = apiFetch("/api/pets/$id")

    fun getFavoritePetIds(): Promise<dynamic> = apiFetch("/api/pets/favorite-ids")
    fun getFavoritePets(): Promise<dynamic> = apiFetch("/api/pets/favorites")
    fun addFavorite(petId: String): Promise<dynamic> = apiFetch("/api/pets/$petId/favorite", js("({method: 'POST'})"))
    fun removeFavorite(petId: String): Promise<dynamic> = apiFetch("/api/pets/$petId/favorite", js("({method: 'DELETE'})"))

    fun getSavedSearches(): Promise<dynamic> = apiFetch("/api/saved-searches")
    fun createSavedSearch(type: String?, country: String): Promise<dynamic> =
        apiFetch("/api/saved-searches", js("({method: 'POST', body: JSON.stringify({type: type, country: country})})"))
    fun deleteSavedSearch(id: Int): Promise<dynamic> = apiFetch("/api/saved-searches/$id", js("({method: 'DELETE'})"))

    fun createPet(pet: dynamic): Promise<dynamic> = apiFetch("/api/pets", js("({method: 'POST', body: JSON.stringify(pet)})"))

    fun updatePet(id: String, pet: dynamic): Promise<dynamic> = apiFetch("/api/pets/$id", js("({method: 'PUT', body: JSON.stringify(pet)})"))

    fun deletePet(id: String): Promise<dynamic> = apiFetch("/api/pets/$id", js("({method: 'DELETE'})"))

    fun updateProfile(displayName: String, country: String? = null): Promise<dynamic> {
        val body = js("({displayName: displayName, country: country})")
        return apiFetch("/api/users/profile", js("({method: 'PUT', body: JSON.stringify(body)})"))
    }

    fun updateLanguage(language: String): Promise<dynamic> {
        val body = js("({language: language})")
        return apiFetch("/api/users/language", js("({method: 'PUT', body: JSON.stringify(body)})"))
    }

    fun getTemporalHome(): Promise<dynamic> = apiFetch("/api/users/temporal-home")

    fun updateTemporalHome(data: dynamic): Promise<dynamic> = apiFetch("/api/users/temporal-home", js("({method: 'PUT', body: JSON.stringify(data)})"))

    fun createTemporalHome(data: dynamic): Promise<dynamic> = apiFetch("/api/users/temporal-home", js("({method: 'POST', body: JSON.stringify(data)})"))

    fun activateTemporalHome(active: Boolean): Promise<dynamic> {
        val body = js("({activate: active})")
        return apiFetch("/api/users/temporal-home-profile", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    fun activateRescuer(active: Boolean): Promise<dynamic> {
        val body = js("({activate: active})")
        return apiFetch("/api/users/rescuer-profile", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    fun activatePhotographer(active: Boolean): Promise<dynamic> {
        val body = js("({activate: active})")
        return apiFetch("/api/photographers/profile", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    fun activateShelter(active: Boolean): Promise<dynamic> {
        val body = js("({activate: active})")
        return apiFetch("/api/users/shelter-profile", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    fun activateSterilization(active: Boolean): Promise<dynamic> {
        val body = js("({activate: active})")
        return apiFetch("/api/users/sterilization-profile", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    fun updatePhotographerSettings(fee: Double, currency: String, country: String, state: String?): Promise<dynamic> {
        val body = js("({photographerFee: fee, photographerCurrency: currency, country: country, state: state})")
        return apiFetch("/api/photographers/settings", js("({method: 'PUT', body: JSON.stringify(body)})"))
    }

    fun getMyPhotographerSettings(): Promise<dynamic> = apiFetch("/api/photographers/me")

    fun getShelter(): Promise<dynamic> = apiFetch("/api/users/shelter")

    fun updateShelter(data: dynamic): Promise<dynamic> = apiFetch("/api/users/shelter", js("({method: 'PUT', body: JSON.stringify(data)})"))

    fun createShelter(data: dynamic): Promise<dynamic> = apiFetch("/api/users/shelter", js("({method: 'POST', body: JSON.stringify(data)})"))

    fun getSterilizationLocation(): Promise<dynamic> = apiFetch("/api/users/sterilization-location")

    fun updateSterilizationLocation(data: dynamic): Promise<dynamic> = apiFetch("/api/users/sterilization-location", js("({method: 'PUT', body: JSON.stringify(data)})"))

    fun createSterilizationLocation(data: dynamic): Promise<dynamic> = apiFetch("/api/users/sterilization-location", js("({method: 'POST', body: JSON.stringify(data)})"))

    fun hasPassword(): Promise<dynamic> = apiFetch("/api/users/has-password")

    fun setPassword(encryptedPassword: String): Promise<dynamic> {
        val body = js("({encryptedPassword: encryptedPassword})")
        return apiFetch("/api/users/password", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    fun changePassword(encryptedCurrentPassword: String, encryptedNewPassword: String): Promise<dynamic> {
        val body = js("({encryptedCurrentPassword: encryptedCurrentPassword, encryptedNewPassword: encryptedNewPassword})")
        return apiFetch("/api/users/password", js("({method: 'PUT', body: JSON.stringify(body)})"))
    }

    fun requestEmailChange(newEmail: String): Promise<dynamic> {
        val body = js("({newEmail: newEmail})")
        return apiFetch("/api/users/request-email-change", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    fun getPhotographers(): Promise<dynamic> = apiFetch("/api/photographers")

    fun getShelters(): Promise<dynamic> = apiFetch("/api/shelters")

    fun getRescuers(): Promise<dynamic> = apiFetch("/api/users/rescuers")

    fun getRescuerById(id: String): Promise<dynamic> = apiFetch("/api/users/rescuers/$id")

    fun applyToVolunteer(rescuerId: Int): Promise<dynamic> {
        val body = js("({rescuerId: rescuerId})")
        return apiFetch("/api/volunteers", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    fun updateVolunteerStatus(id: Int, status: String): Promise<dynamic> {
        val body = js("({status: status})")
        return apiFetch("/api/volunteers/$id/status", js("({method: 'PUT', body: JSON.stringify(body)})"))
    }

    fun getMyVolunteerApplications(): Promise<dynamic> = apiFetch("/api/users/volunteer/applications")

    fun getVolunteerApplicationsForRescuer(): Promise<dynamic> = apiFetch("/api/users/rescuer/volunteers")

    fun createPetEditSuggestion(petId: String, body: dynamic): Promise<dynamic> =
        apiFetch("/api/pets/$petId/edit-suggestions", js("({method: 'POST', body: JSON.stringify(body)})"))

    fun updatePetEditSuggestionStatus(id: Int, status: String): Promise<dynamic> {
        val body = js("({status: status})")
        return apiFetch("/api/pets/edit-suggestions/$id/status", js("({method: 'PUT', body: JSON.stringify(body)})"))
    }

    fun getPetEditSuggestionsForRescuer(): Promise<dynamic> = apiFetch("/api/users/rescuer/edit-suggestions")

    fun getMyPetEditSuggestions(): Promise<dynamic> = apiFetch("/api/users/volunteer/edit-suggestions")

    fun createSponsorshipOffer(body: dynamic): Promise<dynamic> =
        apiFetch("/api/sponsorships", js("({method: 'POST', body: JSON.stringify(body)})"))

    fun markSponsorshipOfferRead(id: Int): Promise<dynamic> =
        apiFetch("/api/sponsorships/$id/read", js("({method: 'PUT'})"))

    fun getSponsorshipOffersForRescuer(): Promise<dynamic> = apiFetch("/api/users/rescuer/sponsorships")

    fun getMySponsorshipOffers(): Promise<dynamic> = apiFetch("/api/users/sponsorships/mine")

    fun getSentPhotographyRequests(): Promise<dynamic> = apiFetch("/api/photographers/requests?scope=sent")

    fun getReceivedPhotographyRequests(): Promise<dynamic> = apiFetch("/api/photographers/requests?scope=received")

    fun updatePhotographyRequestStatus(id: Int, status: String): Promise<dynamic> =
        apiFetch("/api/photographers/requests/$id", js("({method: 'PUT', body: JSON.stringify({status: status})})"))

    fun searchTemporalHomes(query: dynamic): Promise<dynamic> = apiFetch("/api/temporal-homes/search", js("({method: 'POST', body: JSON.stringify(query)})"))

    fun getTemporalHomeById(id: String): Promise<dynamic> = apiFetch("/api/temporal-homes/$id")

    fun sendTemporalHomeRequest(temporalHomeId: Int, message: String): Promise<dynamic> {
        val body = js("({temporalHomeId: temporalHomeId, message: message})")
        return apiFetch("/api/temporal-homes/request", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    fun getMyPets(): Promise<dynamic> = apiFetch("/api/pets/mine")

    fun adoptPet(
        id: String,
        message: String,
        housingType: String? = null,
        hasYard: Boolean? = null,
        hasOtherPets: Boolean? = null,
        experienceLevel: String? = null
    ): Promise<dynamic> {
        val body = js("({message: message, housingType: housingType, hasYard: hasYard, hasOtherPets: hasOtherPets, experienceLevel: experienceLevel})")
        return apiFetch("/api/pets/$id/adopt", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    fun getTemporalHomeRequests(): Promise<dynamic> = apiFetch("/api/users/temporal-home/requests")

    fun blockRescuer(rescuerId: Int): Promise<dynamic> {
        val body = js("({rescuerId: rescuerId})")
        return apiFetch("/api/temporal-homes/block", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    fun createPhotographyRequest(photographerId: Int, petId: Int?, message: String): Promise<dynamic> {
        val body = js("({photographerId: photographerId, petId: petId, message: message})")
        return apiFetch("/api/photographers/requests", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    // Up to 3 photographers per call (PhotographerService enforces the cap and the weekly limit).
    fun createMultiPhotographyRequest(photographerIds: Array<Int>, petId: Int?, message: String): Promise<dynamic> {
        val body = js("({photographerIds: photographerIds, petId: petId, message: message})")
        return apiFetch("/api/photographers/requests/multiple", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    fun getAdoptionRequests(petId: Int): Promise<dynamic> = apiFetch("/api/pets/$petId/adoption-requests")

    fun getRescuerAdoptionRequests(): Promise<dynamic> = apiFetch("/api/users/rescuer/adoption-requests")

    fun getMyAdoptionRequests(): Promise<dynamic> = apiFetch("/api/pets/my-adoption-requests")

    fun updateAdoptionRequest(requestId: Int, status: String, reviewNote: String? = null): Promise<dynamic> {
        val opts = js("({method: 'PUT', headers: {'Content-Type': 'application/x-www-form-urlencoded'}})")
        var formBody = "status=" + window.asDynamic().encodeURIComponent(status)
        if (!reviewNote.isNullOrEmpty()) {
            formBody += "&reviewNote=" + window.asDynamic().encodeURIComponent(reviewNote)
        }
        opts.body = formBody
        return apiFetch("/api/pets/adoption-requests/$requestId", opts)
    }

    fun addImage(petId: String, file: dynamic, isPrimary: Boolean = false, fileName: String? = null): Promise<dynamic> {
        val formData = js("new FormData()")
        val name = fileName ?: (file.name as? String) ?: "photo.jpg"
        formData.append("file", file, name)
        formData.append("isPrimary", isPrimary.toString())
        return window.asDynamic().fetch("/api/pets/$petId/images", js("({method: 'POST', body: formData, credentials: 'include'})")).then { res: dynamic ->
            if (res.ok != true) {
                res.text().then { text: dynamic -> throw js("new Error('Request failed: ' + text)") }
            } else {
                res.json()
            }
        }
    }

    fun removeImage(petId: String, imageId: Int): Promise<dynamic> = apiFetch("/api/pets/$petId/images/$imageId", js("({method: 'DELETE'})"))

    fun addVideo(petId: String, file: dynamic): Promise<dynamic> {
        val formData = js("new FormData()")
        val name = (file.name as? String) ?: "video.mp4"
        formData.append("file", file, name)
        return window.asDynamic().fetch("/api/pets/$petId/video", js("({method: 'POST', body: formData, credentials: 'include'})")).then { res: dynamic ->
            if (res.ok != true) {
                res.text().then { text: dynamic -> throw js("new Error('Request failed: ' + text)") }
            } else {
                res.json()
            }
        }
    }

    fun removeVideo(petId: String): Promise<dynamic> = apiFetch("/api/pets/$petId/video", js("({method: 'DELETE'})"))

    fun setPrimaryImage(petId: String, imageId: Int): Promise<dynamic> = apiFetch("/api/pets/$petId/images/$imageId/primary", js("({method: 'PUT'})"))

    fun getMedicalEvents(petId: String): Promise<dynamic> = apiFetch("/api/pets/$petId/medical-events")

    fun createMedicalEvent(
        petId: String,
        category: String,
        name: String,
        administeredDate: Double,
        nextDueDate: Double?,
        notes: String?
    ): Promise<dynamic> {
        val body = js("({category: category, name: name, administeredDate: administeredDate, nextDueDate: nextDueDate, notes: notes})")
        return apiFetch("/api/pets/$petId/medical-events", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    fun deleteMedicalEvent(eventId: Int): Promise<dynamic> = apiFetch("/api/pets/medical-events/$eventId", js("({method: 'DELETE'})"))

    fun getRescuerMedicalEvents(): Promise<dynamic> = apiFetch("/api/users/rescuer/medical-events")

    fun createFosterPlacement(petId: String, temporalHomeId: Int, notes: String?): Promise<dynamic> {
        val body = js("({temporalHomeId: temporalHomeId, notes: notes})")
        return apiFetch("/api/pets/$petId/foster-placements", js("({method: 'POST', body: JSON.stringify(body)})"))
    }

    fun getFosterPlacementHistory(petId: String): Promise<dynamic> = apiFetch("/api/pets/$petId/foster-placements")

    fun endFosterPlacement(placementId: Int): Promise<dynamic> = apiFetch("/api/pets/foster-placements/$placementId/end", js("({method: 'PUT'})"))

    fun getMyActiveFosterPlacements(): Promise<dynamic> = apiFetch("/api/users/temporal-home/foster-placements")

    fun getPetAnalytics(petId: String): Promise<dynamic> = apiFetch("/api/pets/$petId/analytics")

    fun getRescuerPetAnalytics(): Promise<dynamic> = apiFetch("/api/users/rescuer/pet-analytics")
}
